/**
 * "Abrir outra conta em nova janela" (the account menu; docs/refactor/45): the accounts opened before on this computer,
 * and a new one. Each opens in a window of its own, with its own sign-in, keys and folders.
 */

import { useEffect, useState } from 'react'
import { UserPlus, UserRound } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import { Modal } from './Modal'

export function OtherAccountDialog({
  list,
  open,
  onClose,
}: {
  list: () => Promise<string[]>
  open: (name?: string) => Promise<string>
  onClose: () => void
}) {
  const { t } = useI18n()
  const [names, setNames] = useState<string[]>([])
  const [name, setName] = useState('')
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let alive = true
    void list().then((found) => alive && setNames(found)).catch(() => {})
    return () => {
      alive = false
    }
  }, [list])

  const go = async (which?: string) => {
    setBusy(true)
    setNotice(null)
    try {
      const opened = await open(which)
      setNotice({ ok: true, text: t('otherAccount.opened', { name: opened }) })
      if (!names.includes(opened)) setNames([...names, opened].sort())
      setName('')
    } catch (error) {
      const code = String(error)
      setNotice({ ok: false, text: t(code.includes('already-open') ? 'otherAccount.alreadyOpen' : code.includes('invalid-name') ? 'otherAccount.invalidName' : 'error.generic') })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={t('otherAccount.title')} onClose={onClose}>
      <div className="stack">
        <p className="small muted" style={{ margin: 0 }}>{t('otherAccount.intro')}</p>
        {names.length > 0 && (
          <div className="stack stack--tight">
            {names.map((existing) => (
              <button key={existing} type="button" className="btn" disabled={busy} onClick={() => void go(existing)}>
                <UserRound size={14} aria-hidden /> {t('otherAccount.open', { name: existing })}
              </button>
            ))}
          </div>
        )}
        <div className="row row--tight">
          <input
            className="input"
            value={name}
            maxLength={32}
            placeholder={t('otherAccount.namePlaceholder')}
            aria-label={t('otherAccount.name')}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void go(name.trim() || undefined)}>
            <UserPlus size={14} aria-hidden /> {t('otherAccount.new')}
          </button>
        </div>
        {notice && <div className={`notice small ${notice.ok ? 'notice--ok' : 'notice--warn'}`} role="status">{notice.text}</div>}
        <p className="tiny muted" style={{ margin: 0 }}>{t('otherAccount.limits')}</p>
      </div>
    </Modal>
  )
}
